// src/NewChatApp.tsx
import React, { useEffect, useState } from 'react';
import { ChatPage } from './components/ChatPage';
import Masthead from './layout/Masthead';
import { makeStyles } from '@fluentui/react-components';
import { getBrandConfig } from './brands/brand-registry';
// Import brand-specific favicon functions directly (same pattern as Logo)
import { setBrandAssets, backgroundImageUrl } from '@brand';
import { initializeSession } from './api/api';
import { useLanguage } from './i18n/LanguageProvider';

const useStyles = makeStyles({
  // WCAG 2.2 AAA: Skip to main content link for keyboard navigation
  skipLink: {
    position: "absolute",
    top: "-100px",
    left: "50%",
    transform: "translateX(-50%)",
    backgroundColor: "var(--brand-60)",
    color: "var(--text-on-brand, #FFFFFF)",
    padding: "12px 24px",
    borderRadius: "var(--radius-button, 8px)",
    textDecoration: "none",
    fontWeight: "600",
    fontSize: "14px",
    zIndex: 1000,
    transition: "top 0.2s ease-in-out",
    "&:focus": {
      top: "12px",
      outline: "var(--focus-ring-width, 4px) solid var(--focus-ring-color, var(--brand-60))",
      outlineOffset: "var(--focus-ring-offset, 2px)",
    },
  },
  container: {
    display: "flex",
    flexDirection: "column",
    height: "100dvh", /* Dynamic viewport height - accounts for browser chrome and touch assistance */
    overflow: "hidden",
    position: "relative",
  },
  main: {
    flexGrow: 1,
    minHeight: 0,
    overflow: "hidden",
    display: "flex",
    flexDirection: "column",
    position: "relative",
    zIndex: 1,
  },
});

export const NewChatApp: React.FC = () => {
  const styles = useStyles();
  const { language } = useLanguage();
  const brandConfig = getBrandConfig();
  const backgroundOverlay = brandConfig.layout?.backgroundOverlay;
  const [imageLoaded, setImageLoaded] = useState(!backgroundImageUrl); // If no bg image, start as loaded
  const [sessionReady, setSessionReady] = useState(false);
  const [, setSessionConfigRevision] = useState(0);
  
  // Initialize brand-specific favicon and title
  // Brand is resolved at build time via @brand alias (same pattern as Logo)
  useEffect(() => {
    setBrandAssets();
    
    // Preload background image to prevent flash
    if (backgroundImageUrl) {
      const img = new Image();
      img.onload = () => setImageLoaded(true);
      img.onerror = () => setImageLoaded(true); // Show content even if image fails
      img.src = backgroundImageUrl;
    }
  }, []);

  useEffect(() => {
    let cancelled = false;
    initializeSession(language, true).finally(() => {
      if (cancelled) {
        return;
      }
      setSessionReady(true);
      setSessionConfigRevision(revision => revision + 1);
    });
    return () => {
      cancelled = true;
    };
  }, [language]);
  
  // Don't render until background image is loaded (prevents flash)
  if (!imageLoaded || !sessionReady) {
    return (
      <div style={{ 
        width: '100vw', 
        height: '100dvh', 
        backgroundColor: 'var(--neutral-0, #FFFFFF)' 
      }} />
    );
  }
  
  // Apply background image via inline styles if available
  const backgroundLayers: string[] = [];
  if (backgroundOverlay) {
    backgroundLayers.push(backgroundOverlay);
  }
  if (backgroundImageUrl) {
    backgroundLayers.push(`url(${backgroundImageUrl})`);
  }

  const containerStyle = backgroundLayers.length
    ? {
        backgroundImage: backgroundLayers.join(', '),
        ...(backgroundImageUrl
          ? {
              backgroundSize: 'cover',
              backgroundPosition: 'left center',
              backgroundRepeat: 'no-repeat',
              backgroundAttachment: 'fixed',
            }
          : {}),
      }
    : {};
  
  return (
    <div className={styles.container} style={containerStyle}>
      {/* WCAG 2.2 AAA: Skip to main content link for keyboard users */}
      <a href="#main-content" className={styles.skipLink}>
        {brandConfig.accessibility?.skipLinkText || 'Skip to main content'}
      </a>
      <Masthead />
      <main className={styles.main} role="main" id="main-content" tabIndex={-1}>
        <ChatPage />
      </main>
    </div>
  );
};
